/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.20.0
 * @since 1.20.0
 */

import { Fragment } from 'react';

import { useFormikContext } from 'formik';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { InfoBlock } from '../../../../../../components/common';
import { FormBlockStyled } from '../../../../../../components/common/FormStyled/FormStyled';
import { IOptionItemProps } from '../../../../../../components/common/Select/Select';
import { EColorStatus } from '../../../../../../modules/app/appConstants';
import FormFieldDate from '../../../../../../modules/form/components/FormFieldDate';
import FormFieldInput from '../../../../../../modules/form/components/FormFieldInput';
import FormFieldSelect from '../../../../../../modules/form/components/FormFieldSelect';
import { useSubscriberIdContext } from '../../../../context';
import {
    personalDataDocTypesSelector,
    personalDataOptionsSelector,
    personalDataPassportIdSelector,
    personalDataTranslationSelector,
} from '../../selectors';
import { useRemovePersonalDataAllFilesMutation } from '../../store';
import { IPersonalDataDocType, PersonalDataType, PersonalDataType as T } from '../../types/meta';


interface IPersonalDataEditDocument {
    docTypeSelected?: IPersonalDataDocType;
    setDocTypeSelected: (docType: IPersonalDataDocType) => void;
}

const documentFields = [
    'document.series',
    'document.number',
    'document.dateOfIssue',
    'document.providedByOrganization',
    'document.validFor',
    'document.divisionCode',
    'personalData.nationalityInfo',
];

const PersonalDataEditDocument = ({ docTypeSelected, setDocTypeSelected }: IPersonalDataEditDocument) => {

    const { subscriberId } = useSubscriberIdContext();

    const { getFieldProps, validateForm, setFieldValue, setFieldTouched } = useFormikContext();

    const formFiles = getFieldProps(PersonalDataType.files).value || [];

    const {
        document: { fields: tFields },
        personalDataEdit: tPersonalDataEdit,
    } = useSelector(personalDataTranslationSelector());

    const docTypes = useSelector(personalDataDocTypesSelector(subscriberId));
    const passportId = useSelector(personalDataPassportIdSelector(subscriberId));
    const options = useSelector(personalDataOptionsSelector(subscriberId));

    const [removeAllFiles] = useRemovePersonalDataAllFilesMutation();

    const isDocRequired = (type: T) => docTypeSelected?.mandatoryAttributes.includes(type);

    const onSelectType = (option: IOptionItemProps) => {
        setDocTypeSelected(docTypes[option.value]);
        documentFields.forEach(key => {
            setFieldValue(key, '', false);
            setFieldTouched(key, false, false);
        });
        setTimeout(() => {
            validateForm();
            removeAllFiles(formFiles);
        });
    };

    return (
        <Fragment>
            <FormFieldSelect
                required
                dataName="edit-document-type"
                errorKey="document.type.value"
                label={tFields.type}
                name="document.type"
                options={options}
                placeholder=""
                onSelect={onSelectType}
            />
            {passportId !== docTypeSelected?.id && (
                <FormBlockStyled>
                    <InfoBlock colorStatus={EColorStatus.WARNING}>
                        {tPersonalDataEdit.warning.notPassport}
                    </InfoBlock>
                </FormBlockStyled>
            )}
            {docTypeSelected?.documentAttributes.includes(T.series) && (
                <FormFieldInput
                    dataName="edit-document-series"
                    label={tFields.series}
                    name="document.series"
                    required={isDocRequired(T.series)}
                />
            )}
            {docTypeSelected?.documentAttributes.includes(T.number) && (
                <FormFieldInput
                    dataName="edit-document-number"
                    label={tFields.number}
                    name="document.number"
                    required={isDocRequired(T.number)}
                />
            )}
            {docTypeSelected?.documentAttributes.includes(T.dateOfIssue) && (
                <FormFieldDate
                    dataName="edit-document-dateOfIssue"
                    headType="separate"
                    label={tFields.dateOfIssue}
                    maxDate={moment()}
                    minDate={moment().subtract(100, 'year')}
                    name="document.dateOfIssue"
                    required={isDocRequired(T.dateOfIssue)}
                />
            )}
            {docTypeSelected?.documentAttributes.includes(T.providedByOrganization) && (
                <FormFieldInput
                    dataName="edit-document-providedByOrganization"
                    label={tFields.providedByOrganization}
                    name="document.providedByOrganization"
                    required={isDocRequired(T.providedByOrganization)}
                />
            )}
            {docTypeSelected?.documentAttributes.includes(T.validFor) && (
                <FormFieldDate
                    dataName="edit-document-validFor"
                    headType="separate"
                    label={tFields.validFor}
                    maxDate={moment().add(30, 'year')}
                    minDate={moment().subtract(1, 'year')}
                    name="document.validFor"
                    required={isDocRequired(T.validFor)}
                />
            )}
            {docTypeSelected?.documentAttributes.includes(T.divisionCode) && (
                <FormFieldInput
                    dataName="edit-document-divisionCode"
                    label={tFields.divisionCode}
                    name="document.divisionCode"
                    required={isDocRequired(T.divisionCode)}
                />
            )}
        </Fragment>
    );
};

export default PersonalDataEditDocument;
